import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { alpha, Box, Chip, Divider, IconButton, Typography, useTheme } from "@mui/material";
import Icon from "../../Icon/Icon";

// Severity → theme palette family + stacking priority.
// Lower `order` sits on top of the deck: red first, yellow, then green at the back.
const SEVERITY = {
  high: { tone: "red", order: 0 },
  medium: { tone: "yellow", order: 1 },
  low: { tone: "green", order: 2 },
};

const MAX_CARDS = 5;
const PEEK = 14; // px each card behind the front one sticks out (collapsed)
const CARD_MIN_H = 84; // front card height used to size the collapsed deck

/** One alert rendered as a full colored, clickable card (a summary per type). */
const AlertCard = ({ title, detail, severity, showDetail, theme, onClick }) => {
  const [t] = useTranslation("dashboard");
  const { tone } = SEVERITY[severity] || SEVERITY.medium;
  const accent = theme.palette[tone]?.main;
  const soft = theme.palette[`${tone}V`]?.main;
  const clickable = typeof onClick === "function";

  return (
    <Box
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      sx={{
        boxSizing: "border-box",
        minHeight: CARD_MIN_H,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.75,
        borderRadius: 2,
        borderLeft: `5px solid ${accent}`,
        bgcolor: soft,
        boxShadow: "0 6px 16px rgba(0,0,0,0.10)",
        ...(clickable && {
          cursor: "pointer",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          "&:hover": { transform: "translateY(-2px)", boxShadow: "0 10px 22px rgba(0,0,0,0.14)" },
          "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: 2 },
        }),
      }}
    >
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
          {title}
        </Typography>
        {showDetail && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {detail}
          </Typography>
        )}
      </Box>
      <Chip
        size="small"
        label={t(`static_dashboard.severity_${severity}`, severity)}
        sx={{
          flexShrink: 0,
          height: 20,
          bgcolor: accent,
          color: "#fff",
          textTransform: "capitalize",
          fontWeight: 600,
          fontSize: 11,
          borderRadius: 1,
          "& .MuiChip-label": { px: 1 },
        }}
      />
    </Box>
  );
};

/**
 * AlertStack — priority alerts shown as a physical deck of cards, one on top of
 * another (max 5). Ordered by severity so red cards are always on top, yellow in
 * the middle and green at the back. Collapsed by default (front card readable,
 * the rest peek from behind); toggling expands them into a readable list.
 */
export const AlertStack = ({ title, iconName = "bold_warning_2", alerts = [] }) => {
  const [t] = useTranslation("dashboard");
  const theme = useTheme();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const goTo = (route) => route && navigate(route);

  // Sort by severity priority, then keep the top MAX_CARDS.
  const cards = useMemo(() => {
    return [...alerts]
      .sort(
        (a, b) =>
          (SEVERITY[a.severity]?.order ?? 1) - (SEVERITY[b.severity]?.order ?? 1)
      )
      .slice(0, MAX_CARDS);
  }, [alerts]);

  const count = cards.length;
  // Height the collapsed deck needs: front card + the peeking edges behind it.
  const collapsedHeight = CARD_MIN_H + (count - 1) * PEEK;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Icon name={iconName} size={22} color={theme.palette.red?.main} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {count > 0 && (
          <Box
            sx={{
              position: "relative",
              minWidth: 26,
              height: 26,
              px: 0.75,
              borderRadius: "999px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 800,
              lineHeight: 1,
              color: "#fff",
              background: `linear-gradient(135deg, ${theme.palette.red?.main} 0%, #b52d25 100%)`,
              boxShadow: `0 3px 8px ${alpha(theme.palette.red?.main || "#de463d", 0.5)}`,
              // Pulsing ring to convey urgency.
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                border: `2px solid ${theme.palette.red?.main}`,
                animation: "alertPulse 1.8s ease-out infinite",
              },
              "@keyframes alertPulse": {
                "0%": { transform: "scale(1)", opacity: 0.7 },
                "70%": { transform: "scale(1.55)", opacity: 0 },
                "100%": { transform: "scale(1.55)", opacity: 0 },
              },
            }}
          >
            {count}
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {count > 1 && (
          <IconButton
            size="small"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? t("static_dashboard.collapse") : t("static_dashboard.expand")}
          >
            <Icon
              name={expanded ? "outline_arrow_up" : "outline_arrow_down"}
              size={18}
              color={theme.palette.text.secondary}
            />
          </IconButton>
        )}
      </Box>
      <Divider sx={{ mb: 1.5 }} />

      {count === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
          {t("static_dashboard.empty_alerts")}
        </Typography>
      ) : expanded ? (
        // Expanded: normal readable list, still ordered by severity. Each card
        // navigates to its own filtered report list.
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {cards.map((a) => (
            <AlertCard
              key={a.id}
              {...a}
              severity={a.severity}
              showDetail
              theme={theme}
              onClick={() => goTo(a.route)}
            />
          ))}
        </Box>
      ) : (
        // Collapsed: a physical deck. Front card (most severe) fully visible,
        // the rest peek from behind, scaled and faded to convey depth. With a
        // single alert the deck click navigates; with several it expands first.
        <Box
          onClick={() => (count > 1 ? setExpanded(true) : goTo(cards[0]?.route))}
          sx={{
            position: "relative",
            height: collapsedHeight,
            cursor: "pointer",
          }}
        >
          {cards.map((a, i) => (
            <Box
              key={a.id}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                zIndex: count - i, // front card (i=0) on top
                transform: `translateY(${i * PEEK}px) scale(${1 - i * 0.045})`,
                transformOrigin: "top center",
                opacity: 1 - i * 0.12,
                transition: "transform 0.25s ease, opacity 0.25s ease",
                pointerEvents: "none",
              }}
            >
              <AlertCard {...a} severity={a.severity} showDetail={i === 0} theme={theme} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AlertStack;
